
import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
} from "react";

import Swal from "sweetalert2";

import {
    Box,
    Button,
    Paper,
    IconButton,
    TablePagination,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Snackbar,
    Alert,
    Autocomplete,
    Typography,
    Stack,
    Chip,
    InputAdornment,
    Tooltip,
    Divider,
    Checkbox,
    CircularProgress,
} from "@mui/material";

import {
    Delete,
    Edit,
    Search,
    School,
    PictureAsPdf,
    FilterAlt,
    Refresh,
    Assessment,
    CalendarMonth,
    MenuBook,
    PersonOutline,
    VerifiedOutlined,
    LockOutlined,
} from "@mui/icons-material";

import AddAndUpDateGrades from "./AddAndUpDateGrades";
import GradesDistributionPdf from "./GradesDistributionPdf";

import api from "../../api/api";
import { useAcademicYear } from "../../AcademicYearContext";

const COLORS = {
    navy: "#062A4B",
    deepBlue: "#063A6B",
    gold: "#F6C944",
    goldenYellow: "#EFA81C",
    orange: "#C07500",
    background: "#F3F6FB",
    white: "#FFFFFF",
    text: "#243447",
    muted: "#718096",
    border: "#E2E8F0",
    success: "#15803D",
    danger: "#DC2626",
};

const GradeDistribution = () => {
    const token = localStorage.getItem("token");

    // Use the context only to obtain available academic years.
    // Never use its setter to change the global selection.
    const {
        academicYears = [],
        loading: academicYearsLoading,
        initialized: academicYearsInitialized,
    } = useAcademicYear();

    // Independent, page-specific academic year.
    const [selectedYear, setSelectedYear] = useState(null);

    const [userType, setUserType] = useState("");
    const [gradeDistributions, setGradeDistributions] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [gradeClasses, setGradeClasses] = useState([]);
    const [periods, setPeriods] = useState([]);

    const [searchTerm, setSearchTerm] = useState("");
    const [totalCount, setTotalCount] = useState(0);

    const [pdfGradeClass, setPdfGradeClass] = useState(null);
    const [pdfSubject, setPdfSubject] = useState(null);
    const [pdfPeriod, setPdfPeriod] = useState(null);

    const [editGradeId, setEditGradeId] = useState(null);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertSeverity, setAlertSeverity] = useState("success");

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [tableLoading, setTableLoading] = useState(false);

    // Prevent outdated requests from replacing newer results.
    const gradeRequestId = useRef(0);

    // --------------------------------------------------
    // INITIALIZE THE LOCAL YEAR
    // --------------------------------------------------
    useEffect(() => {
        if (!academicYearsInitialized) return;

        setSelectedYear((currentYear) => {
            // Preserve the page's selection when the context
            // refreshes its list of academic years.
            const existingYear = academicYears.find(
                (year) => year.id === currentYear?.id
            );

            if (existingYear) return existingYear;

            // Initial preference: active year, then first year.
            return (
                academicYears.find((year) => year.is_active === true) ||
                academicYears[0] ||
                null
            );
        });
    }, [academicYears, academicYearsInitialized]);

    // --------------------------------------------------
    // FETCH USER TYPE
    // --------------------------------------------------
    useEffect(() => {
        let mounted = true;

        const fetchUserType = async () => {
            try {
                const { data } = await api.get("/user-profile/", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (mounted) {
                    setUserType(
                        String(data?.user_type || "").toLowerCase()
                    );
                }
            } catch (error) {
                console.error("User type error:", error);
            }
        };

        fetchUserType();

        return () => {
            mounted = false;
        };
    }, [token]);

    // --------------------------------------------------
    // LOAD SUPPORTING DATA
    // --------------------------------------------------
    useEffect(() => {
        let mounted = true;

        const loadSupportingData = async () => {
            const headers = {
                Authorization: `Bearer ${token}`,
            };

            const requests = [
                api.get("/assigned-subjects/", { headers }),
                api.get("/assigned-grade-classes/", { headers }),
                api.get("/api/users/periods/", { headers }),
            ];

            const results = await Promise.allSettled(requests);

            if (!mounted) return;

            if (results[0].status === "fulfilled") {
                const data = results[0].value.data;
                setSubjects(Array.isArray(data) ? data : data?.results || []);
            } else {
                console.error("Subjects error:", results[0].reason);
            }

            if (results[1].status === "fulfilled") {
                const data = results[1].value.data;
                setGradeClasses(
                    Array.isArray(data) ? data : data?.results || []
                );
            } else {
                console.error("Grade classes error:", results[1].reason);
            }

            if (results[2].status === "fulfilled") {
                const data = results[2].value.data;
                setPeriods(Array.isArray(data) ? data : data?.results || []);
            } else {
                console.error("Periods error:", results[2].reason);
            }
        };

        loadSupportingData();

        return () => {
            mounted = false;
        };
    }, [token]);

    // --------------------------------------------------
    // FETCH GRADE DISTRIBUTIONS
    // Uses only the locally selected academic year.
    // --------------------------------------------------
    const fetchGradeDistributions = useCallback(
        async (
            pageNumber = page + 1,
            pageSize = rowsPerPage,
            search = searchTerm
        ) => {
            const requestId = ++gradeRequestId.current;

            if (!selectedYear?.id) {
                setGradeDistributions([]);
                setTotalCount(0);
                setTableLoading(false);
                return;
            }

            setTableLoading(true);

            try {
                const response = await api.get(
                    "/api/users/grades-distribution/",
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        params: {
                            academic_year: selectedYear.id,
                            page: pageNumber,
                            page_size: pageSize,
                            search,
                        },
                    }
                );

                // Ignore results from an older request.
                if (requestId !== gradeRequestId.current) return;

                const data = response.data;

                setGradeDistributions(
                    Array.isArray(data?.results) ? data.results : []
                );

                setTotalCount(Number(data?.count || 0));
            } catch (error) {
                if (requestId !== gradeRequestId.current) return;

                console.error("Loading grades error:", error);

                setGradeDistributions([]);
                setTotalCount(0);

                setAlertMessage(
                    "Unable to load grade records for this academic year."
                );
                setAlertSeverity("error");
                setSnackbarOpen(true);
            } finally {
                if (requestId === gradeRequestId.current) {
                    setTableLoading(false);
                }
            }
        },
        [selectedYear?.id, token, page, rowsPerPage, searchTerm]
    );

    // Reload records whenever the local year or table controls change.
    useEffect(() => {
        if (!academicYearsInitialized || !selectedYear?.id) return;

        fetchGradeDistributions(page + 1, rowsPerPage, searchTerm);

        return () => {
            // Invalidate requests when the year, page, or search changes.
            gradeRequestId.current += 1;
        };
    }, [
        academicYearsInitialized,
        selectedYear?.id,
        page,
        rowsPerPage,
        searchTerm,
        fetchGradeDistributions,
    ]);

    // --------------------------------------------------
    // LOCAL YEAR CHANGE
    // --------------------------------------------------
    const handleYearChange = (_, newYear) => {
        if (!newYear || newYear.id === selectedYear?.id) return;

        // Deliberately do not call context.setSelectedYear().
        setSelectedYear(newYear);

        setPage(0);
        setSearchTerm("");
        setEditGradeId(null);

        setPdfGradeClass(null);
        setPdfSubject(null);
        setPdfPeriod(null);

        setGradeDistributions([]);
        setTotalCount(0);
    };

    // --------------------------------------------------
    // DELETE GRADE RECORD
    // --------------------------------------------------
    const handleDelete = async (id) => {
        if (userType === "teacher") {
            await Swal.fire(
                "Permission Denied",
                "Teachers cannot delete grade records.",
                "error"
            );
            return;
        }

        const result = await Swal.fire({
            title: "Delete grade record?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete record",
            cancelButtonText: "Cancel",
            confirmButtonColor: COLORS.danger,
            cancelButtonColor: COLORS.navy,
            reverseButtons: true,
            focusCancel: true,
        });

        if (!result.isConfirmed) return;

        try {
            await api.delete(
                `/api/users/grades-distribution/${id}/`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    params: {
                        academic_year: selectedYear?.id,
                    },
                }
            );

            setAlertMessage("Grade record deleted successfully.");
            setAlertSeverity("success");

            // If the last row on a page was deleted, move back one page.
            if (gradeDistributions.length === 1 && page > 0) {
                setPage((currentPage) => currentPage - 1);
            } else {
                await fetchGradeDistributions(
                    page + 1,
                    rowsPerPage,
                    searchTerm
                );
            }
        } catch (error) {
            console.error("Delete grade error:", error);

            setAlertMessage(
                error.response?.data?.detail ||
                    "Unable to delete this grade record."
            );
            setAlertSeverity("error");
        } finally {
            setSnackbarOpen(true);
        }
    };

    // --------------------------------------------------
    // TEACHER EDIT PERMISSION
    // --------------------------------------------------
    const handleTeacherPermission = async (item, value) => {
        try {
            await api.patch(
                `/api/users/grades-distribution/${item.id}/`,
                {
                    allow_teacher_update: value,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    params: {
                        academic_year: selectedYear?.id,
                    },
                }
            );

            setGradeDistributions((previous) =>
                previous.map((grade) =>
                    grade.id === item.id
                        ? { ...grade, allow_teacher_update: value }
                        : grade
                )
            );

            setAlertMessage(
                `Teacher editing ${value ? "enabled" : "disabled"} successfully.`
            );
            setAlertSeverity("success");
            setSnackbarOpen(true);
        } catch (error) {
            console.error("Teacher permission update error:", error);

            await Swal.fire(
                "Update Failed",
                error.response?.data?.detail ||
                    "Could not update teacher editing permission.",
                "error"
            );
        }
    };

    // --------------------------------------------------
    // PAGINATION
    // --------------------------------------------------
    const handlePageChange = (_, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // --------------------------------------------------
    // SCORE PRESENTATION
    // --------------------------------------------------
    const getFinalScoreStyle = (score) => {
        const numericScore = Number(score);
        const isNumeric =
            score !== null &&
            score !== "" &&
            Number.isFinite(numericScore);

        const letterScore =
            typeof score === "string" ? score.trim().toUpperCase() : "";

        const isFailing =
            (isNumeric && numericScore < 70) ||
            ["D", "F"].includes(letterScore);

        return {
            color: isFailing ? COLORS.danger : COLORS.deepBlue,
            backgroundColor: isFailing ? "#FEF2F2" : "#EFF6FF",
        };
    };

    // --------------------------------------------------
    // SHARED STYLES
    // --------------------------------------------------
    const filterInputSx = {
        width: "100%",
        "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
            backgroundColor: COLORS.white,
            minHeight: 48,
            "& fieldset": { borderColor: COLORS.border },
            "&:hover fieldset": { borderColor: COLORS.deepBlue },
            "&.Mui-focused fieldset": {
                borderColor: COLORS.goldenYellow,
                borderWidth: 2,
            },
        },
        "& .MuiInputLabel-root.Mui-focused": {
            color: COLORS.deepBlue,
        },
    };

    const headerCellSx = {
        backgroundColor: COLORS.navy,
        color: COLORS.white,
        fontWeight: 800,
        fontSize: "11px",
        textTransform: "uppercase",
        letterSpacing: "0.55px",
        whiteSpace: "nowrap",
        py: 2,
        px: 2,
        borderBottom: `3px solid ${COLORS.gold}`,
    };

    const bodyCellSx = {
        fontSize: "12.5px",
        color: COLORS.text,
        py: 1.6,
        px: 2,
        whiteSpace: "nowrap",
        borderBottom: `1px solid ${COLORS.border}`,
    };

    const selectedYearName =
        selectedYear?.name ||
        selectedYear?.year ||
        selectedYear?.academic_year ||
        "Not selected";

    const canManageTeacherPermission = [
        "admin",
        "vpi",
        "vpa",
    ].includes(userType);

    return (
        <Box
            sx={{
                minHeight: "100vh",
                width: "100%",
                boxSizing: "border-box",
                bgcolor: COLORS.background,
                p: { xs: 1.25, sm: 2, md: 3, lg: 3.5 },
                "& *": { boxSizing: "border-box" },
            }}
        >
            {/* PAGE HEADER */}
            <Paper
                elevation={0}
                sx={{
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: { xs: "14px", md: "20px" },
                    mb: 2.5,
                    background: `linear-gradient(120deg, ${COLORS.navy} 0%, ${COLORS.deepBlue} 72%, #0A4978 100%)`,
                    color: COLORS.white,
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        right: -30,
                        top: -100,
                        width: 260,
                        height: 260,
                        borderRadius: "50%",
                        border: "35px solid rgba(246,201,68,0.10)",
                        pointerEvents: "none",
                    }}
                />

                <Box sx={{ position: "relative", zIndex: 1, p: { xs: 2, sm: 3, md: 3.5 } }}>
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        alignItems={{ xs: "stretch", md: "center" }}
                        justifyContent="space-between"
                        spacing={2.5}
                    >
                        <Stack direction="row" spacing={1.75} alignItems="center">
                            <Box
                                sx={{
                                    width: 54,
                                    height: 54,
                                    flexShrink: 0,
                                    borderRadius: "15px",
                                    bgcolor: COLORS.gold,
                                    color: COLORS.navy,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Assessment sx={{ fontSize: 32 }} />
                            </Box>

                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: { xs: 21, md: 29 },
                                        lineHeight: 1.2,
                                        fontWeight: 850,
                                        letterSpacing: "-0.65px",
                                    }}
                                >
                                    Grade Distribution
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.75,
                                        color: "rgba(255,255,255,0.76)",
                                        fontSize: 12,
                                        lineHeight: 1.6,
                                    }}
                                >
                                    Academic performance, assessment records and grading management
                                </Typography>
                            </Box>
                        </Stack>

                        <Box
                            sx={{
                                width: { xs: "100%", md: 310 },
                                p: 1.5,
                                borderRadius: "12px",
                                bgcolor: "rgba(255,255,255,0.09)",
                                border: "1px solid rgba(255,255,255,0.16)",
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                <CalendarMonth sx={{ color: COLORS.gold }} />
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        color: "rgba(255,255,255,0.8)",
                                        fontWeight: 750,
                                    }}
                                >
                                    PAGE-SPECIFIC ACADEMIC YEAR
                                </Typography>
                            </Stack>

                            <Autocomplete
                                fullWidth
                                size="small"
                                options={academicYears}
                                value={selectedYear}
                                loading={academicYearsLoading}
                                getOptionLabel={(option) => option?.name || ""}
                                isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                }
                                onChange={handleYearChange}
                                disableClearable
                                disabled={
                                    !academicYearsInitialized ||
                                    academicYears.length === 0
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select academic year"
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                bgcolor: COLORS.white,
                                                color: COLORS.navy,
                                                borderRadius: "9px",
                                            },
                                            "& .MuiInputBase-input": {
                                                fontWeight: 800,
                                            },
                                        }}
                                    />
                                )}
                                noOptionsText="No academic years available"
                            />

                            <Typography
                                sx={{
                                    mt: 1,
                                    color: "rgba(255,255,255,0.65)",
                                    fontSize: 10,
                                }}
                            >
                                Changing this year affects this page only.
                            </Typography>
                        </Box>
                    </Stack>

                    <Divider sx={{ my: 2.5, borderColor: "rgba(255,255,255,0.13)" }} />

                    <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
                        <Chip
                            icon={<School sx={{ color: `${COLORS.gold} !important` }} />}
                            label="Academic Records"
                            size="small"
                            sx={{
                                bgcolor: "rgba(255,255,255,0.09)",
                                color: COLORS.white,
                                fontWeight: 700,
                            }}
                        />

                        <Chip
                            icon={<VerifiedOutlined sx={{ color: "#86EFAC !important" }} />}
                            label="Live Server Data"
                            size="small"
                            sx={{
                                bgcolor: "rgba(255,255,255,0.09)",
                                color: COLORS.white,
                                fontWeight: 700,
                            }}
                        />

                        <Typography
                            sx={{
                                ml: { xs: 0, md: "auto" },
                                color: "rgba(255,255,255,0.67)",
                                fontSize: 10,
                                fontWeight: 600,
                            }}
                        >
                            {userType
                                ? `Access: ${userType.replace(/_/g, " ")}`
                                : "Academic Management"}
                        </Typography>
                    </Stack>
                </Box>
            </Paper>

            {/* SEARCH AND FILTERS */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: "16px",
                    border: `1px solid ${COLORS.border}`,
                    bgcolor: COLORS.white,
                    mb: 2.5,
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        px: { xs: 2, md: 2.5 },
                        py: 2,
                        borderBottom: `1px solid ${COLORS.border}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                    }}
                >
                    <Box
                        sx={{
                            width: 38,
                            height: 38,
                            borderRadius: "10px",
                            bgcolor: "#EFF6FF",
                            color: COLORS.deepBlue,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <FilterAlt />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ color: COLORS.navy, fontWeight: 850, fontSize: 15 }}>
                            Records &amp; Filters
                        </Typography>
                        <Typography sx={{ color: COLORS.muted, fontSize: 11, mt: 0.35 }}>
                            Search records and prepare filtered grade reports.
                        </Typography>
                    </Box>

                    <Tooltip title="Refresh current results">
                        <span>
                            <IconButton
                                onClick={() =>
                                    fetchGradeDistributions(
                                        page + 1,
                                        rowsPerPage,
                                        searchTerm
                                    )
                                }
                                disabled={tableLoading || !selectedYear?.id}
                                sx={{
                                    color: COLORS.deepBlue,
                                    border: `1px solid ${COLORS.border}`,
                                    borderRadius: "10px",
                                    width: 40,
                                    height: 40,
                                }}
                            >
                                {tableLoading
                                    ? <CircularProgress size={19} />
                                    : <Refresh />}
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>

                <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "minmax(0, 1fr)",
                                sm: "repeat(2, minmax(0, 1fr))",
                                lg: "minmax(240px, 1.35fr) repeat(3, minmax(150px, 1fr))",
                            },
                            gap: 1.5,
                            alignItems: "start",
                        }}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            label="Search grade records"
                            placeholder="Student, grade, course..."
                            value={searchTerm}
                            onChange={(event) => {
                                setSearchTerm(event.target.value);
                                setPage(0);
                            }}
                            sx={filterInputSx}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ color: COLORS.muted, fontSize: 21 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Autocomplete
                            fullWidth
                            size="small"
                            options={gradeClasses}
                            value={pdfGradeClass}
                            onChange={(_, value) => setPdfGradeClass(value || null)}
                            getOptionLabel={(option) => option?.name || ""}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => (
                                <TextField {...params} label="Grade Class" sx={filterInputSx} />
                            )}
                        />

                        <Autocomplete
                            fullWidth
                            size="small"
                            options={subjects}
                            value={pdfSubject}
                            onChange={(_, value) => setPdfSubject(value || null)}
                            getOptionLabel={(option) => option?.name || ""}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => (
                                <TextField {...params} label="Course" sx={filterInputSx} />
                            )}
                        />

                        <Autocomplete
                            fullWidth
                            size="small"
                            options={periods}
                            value={pdfPeriod}
                            onChange={(_, value) => setPdfPeriod(value || null)}
                            getOptionLabel={(option) => option?.name || ""}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => (
                                <TextField {...params} label="Period" sx={filterInputSx} />
                            )}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: { xs: "stretch", sm: "center" },
                            justifyContent: "space-between",
                            flexDirection: { xs: "column", sm: "row" },
                            gap: 1.5,
                            mt: 2,
                        }}
                    >
                        <AddAndUpDateGrades
                            token={token}
                            userType={userType}
                            gradeDistributions={gradeDistributions}
                            gradeClasses={gradeClasses}
                            subjects={subjects}
                            periods={periods}
                            selectedYear={selectedYear}
                            editGradeId={editGradeId}
                            setEditGradeId={setEditGradeId}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            searchTerm={searchTerm}
                            fetchGradeDistributions={fetchGradeDistributions}
                            setAlertMessage={setAlertMessage}
                            setAlertSeverity={setAlertSeverity}
                            setSnackbarOpen={setSnackbarOpen}
                        />

                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            sx={{
                                width: { xs: "100%", sm: "auto" },
                                "& > *": { maxWidth: "100%" },
                            }}
                        >
                            <GradesDistributionPdf
                                pdfGradeClass={pdfGradeClass}
                                pdfSubject={pdfSubject}
                                pdfPeriod={pdfPeriod}
                                selectedYear={selectedYear}
                                token={token}
                                setAlertMessage={setAlertMessage}
                                setAlertSeverity={setAlertSeverity}
                                setSnackbarOpen={setSnackbarOpen}
                            />

                            <Button
                                variant="contained"
                                startIcon={<PictureAsPdf />}
                                onClick={() =>
                                    document
                                        .querySelector("#grades-pdf-download")
                                        ?.click()
                                }
                                sx={{
                                    minHeight: 44,
                                    px: 2.5,
                                    borderRadius: "10px",
                                    textTransform: "none",
                                    fontWeight: 800,
                                    fontSize: 12,
                                    whiteSpace: "nowrap",
                                    bgcolor: COLORS.navy,
                                    color: COLORS.white,
                                    boxShadow: "none",
                                    "&:hover": { bgcolor: COLORS.deepBlue },
                                }}
                            >
                                Save PDF
                            </Button>
                        </Stack>
                    </Box>
                </Box>
            </Paper>

            {/* GRADE RECORDS */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: "16px",
                    border: `1px solid ${COLORS.border}`,
                    overflow: "hidden",
                    bgcolor: COLORS.white,
                }}
            >
                <Box
                    sx={{
                        p: { xs: 2, md: 2.5 },
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "flex-start", sm: "center" },
                        justifyContent: "space-between",
                        gap: 1.5,
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Box
                            sx={{
                                width: 42,
                                height: 42,
                                borderRadius: "11px",
                                bgcolor: "#FFF7DB",
                                color: COLORS.orange,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <MenuBook />
                        </Box>

                        <Box>
                            <Typography sx={{ color: COLORS.navy, fontSize: 16, fontWeight: 850 }}>
                                Grade Records
                            </Typography>
                            <Typography sx={{ mt: 0.35, color: COLORS.muted, fontSize: 11 }}>
                                {selectedYearName} · Assessment scores and final results
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
                        <Chip
                            size="small"
                            label={`${gradeDistributions.length} displayed`}
                            sx={{ fontWeight: 750, bgcolor: "#EFF6FF", color: COLORS.deepBlue }}
                        />
                        <Chip
                            size="small"
                            label={`${totalCount.toLocaleString()} total`}
                            sx={{ fontWeight: 750, bgcolor: "#FFF7DB", color: COLORS.orange }}
                        />
                    </Stack>
                </Box>

                <Divider sx={{ borderColor: COLORS.border }} />

                <TableContainer
                    sx={{
                        width: "100%",
                        overflowX: "auto",
                        position: "relative",
                        maxHeight: { xs: "none", md: "calc(100vh - 340px)" },
                        minHeight: 180,
                        "&::-webkit-scrollbar": { height: 8, width: 8 },
                        "&::-webkit-scrollbar-thumb": {
                            bgcolor: "#CBD5E1",
                            borderRadius: 10,
                        },
                    }}
                >
                    <Table
                        stickyHeader
                        size="small"
                        aria-label="Grade distribution records"
                        sx={{
                            minWidth: 1500,
                            tableLayout: "auto",
                            borderCollapse: "separate",
                            borderSpacing: 0,
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                {[
                                    "Grade Class",
                                    "Period",
                                    "Student",
                                    "Course",
                                    "Quiz",
                                    "Assignment",
                                    "Participation",
                                    "Test",
                                    "Scale",
                                    "Final",
                                    "Remark",
                                    "Teacher Edit",
                                    "Actions",
                                ].map((head) => (
                                    <TableCell
                                        key={head}
                                        align={[
                                            "Quiz",
                                            "Assignment",
                                            "Participation",
                                            "Test",
                                            "Scale",
                                            "Final",
                                        ].includes(head) ? "center" : "left"}
                                        sx={headerCellSx}
                                    >
                                        {head}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {tableLoading || !academicYearsInitialized ? (
                                <TableRow>
                                    <TableCell colSpan={13} align="center" sx={{ py: 8 }}>
                                        <CircularProgress size={34} sx={{ color: COLORS.deepBlue }} />
                                        <Typography sx={{ mt: 1.5, color: COLORS.muted, fontSize: 12 }}>
                                            Loading grade records...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : !selectedYear ? (
                                <TableRow>
                                    <TableCell colSpan={13} align="center" sx={{ py: 7 }}>
                                        <CalendarMonth sx={{ fontSize: 42, color: COLORS.muted }} />
                                        <Typography sx={{ mt: 1, fontWeight: 800, color: COLORS.navy }}>
                                            No academic year available
                                        </Typography>
                                        <Typography sx={{ mt: 0.5, fontSize: 12, color: COLORS.muted }}>
                                            Please ensure academic years have been configured.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : gradeDistributions.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={13} align="center" sx={{ py: 8 }}>
                                        <Assessment sx={{ fontSize: 42, color: COLORS.muted }} />
                                        <Typography sx={{ mt: 1.5, fontWeight: 850, color: COLORS.navy }}>
                                            No grade records found
                                        </Typography>
                                        <Typography sx={{ mt: 0.5, fontSize: 12, color: COLORS.muted }}>
                                            {searchTerm
                                                ? "Try a different search term."
                                                : `No records are available for ${selectedYearName}.`}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                gradeDistributions.map((item, index) => {
                                    const scoreStyle = getFinalScoreStyle(item.final_score);

                                    return (
                                        <TableRow
                                            key={item.id}
                                            hover
                                            sx={{
                                                bgcolor:
                                                    userType === "teacher" && item.allow_teacher_update
                                                        ? "#F0F7FF"
                                                        : index % 2 === 0
                                                            ? COLORS.white
                                                            : "#FAFCFF",
                                                "&:hover": { bgcolor: "#FFF9E8 !important" },
                                                "& td:first-of-type": {
                                                    borderLeft:
                                                        userType === "teacher" && item.allow_teacher_update
                                                            ? `3px solid ${COLORS.deepBlue}`
                                                            : "3px solid transparent",
                                                },
                                            }}
                                        >
                                            <TableCell sx={bodyCellSx}>
                                                <Typography sx={{ fontSize: 12, fontWeight: 750, color: COLORS.navy }}>
                                                    {item.grade_class_name || "—"}
                                                </Typography>
                                            </TableCell>

                                            <TableCell sx={bodyCellSx}>
                                                {item.period_name || "—"}
                                            </TableCell>

                                            <TableCell sx={bodyCellSx}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Box
                                                        sx={{
                                                            width: 30,
                                                            height: 30,
                                                            borderRadius: "9px",
                                                            bgcolor: "#EAF1F9",
                                                            color: COLORS.deepBlue,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        <PersonOutline sx={{ fontSize: 19 }} />
                                                    </Box>
                                                    <Typography sx={{ fontSize: 12, fontWeight: 750, color: COLORS.text }}>
                                                        {item.student_name || "—"}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>

                                            <TableCell sx={bodyCellSx}>
                                                {item.subject_name || "—"}
                                            </TableCell>

                                            <TableCell align="center" sx={bodyCellSx}>
                                                {item.quiz_score ?? "—"}
                                            </TableCell>
                                            <TableCell align="center" sx={bodyCellSx}>
                                                {item.assignment_score ?? "—"}
                                            </TableCell>
                                            <TableCell align="center" sx={bodyCellSx}>
                                                {item.participation_score ?? "—"}
                                            </TableCell>
                                            <TableCell align="center" sx={bodyCellSx}>
                                                {item.test_score ?? "—"}
                                            </TableCell>

                                            <TableCell align="center" sx={bodyCellSx}>
                                                <Chip
                                                    size="small"
                                                    label={item.scale_grade ?? "—"}
                                                    sx={{
                                                        height: 25,
                                                        minWidth: 42,
                                                        fontSize: 11,
                                                        fontWeight: 850,
                                                        bgcolor: "#FFF7DB",
                                                        color: COLORS.orange,
                                                    }}
                                                />
                                            </TableCell>

                                            <TableCell align="center" sx={bodyCellSx}>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        minWidth: 48,
                                                        px: 1,
                                                        py: 0.65,
                                                        borderRadius: "7px",
                                                        fontWeight: 900,
                                                        fontSize: 12,
                                                        ...scoreStyle,
                                                    }}
                                                >
                                                    {item.final_score ?? "—"}
                                                </Box>
                                            </TableCell>

                                            <TableCell sx={bodyCellSx}>
                                                <Typography
                                                    sx={{
                                                        fontSize: 11,
                                                        maxWidth: 180,
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                    }}
                                                    title={item.remark || ""}
                                                >
                                                    {item.remark || "—"}
                                                </Typography>
                                            </TableCell>

                                            <TableCell align="center" sx={bodyCellSx}>
                                                {canManageTeacherPermission ? (
                                                    <Tooltip
                                                        title={
                                                            item.allow_teacher_update
                                                                ? "Disable teacher editing"
                                                                : "Allow teacher editing"
                                                        }
                                                    >
                                                        <Checkbox
                                                            size="small"
                                                            checked={Boolean(item.allow_teacher_update)}
                                                            onChange={(event) =>
                                                                handleTeacherPermission(
                                                                    item,
                                                                    event.target.checked
                                                                )
                                                            }
                                                            sx={{
                                                                color: "#94A3B8",
                                                                "&.Mui-checked": {
                                                                    color: COLORS.deepBlue,
                                                                },
                                                            }}
                                                        />
                                                    </Tooltip>
                                                ) : (
                                                    <Chip
                                                        size="small"
                                                        icon={
                                                            item.allow_teacher_update
                                                                ? <VerifiedOutlined sx={{ fontSize: "14px !important" }} />
                                                                : <LockOutlined sx={{ fontSize: "14px !important" }} />
                                                        }
                                                        label={item.allow_teacher_update ? "Allowed" : "Locked"}
                                                        sx={{
                                                            height: 24,
                                                            fontSize: 10,
                                                            fontWeight: 750,
                                                            bgcolor: item.allow_teacher_update ? "#DCFCE7" : "#F1F5F9",
                                                            color: item.allow_teacher_update ? COLORS.success : COLORS.muted,
                                                            "& .MuiChip-icon": { color: "inherit" },
                                                        }}
                                                    />
                                                )}
                                            </TableCell>

                                            <TableCell sx={bodyCellSx}>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <Tooltip title="Edit grade record">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setEditGradeId(item.id)}
                                                            sx={{
                                                                width: 33,
                                                                height: 33,
                                                                color: COLORS.deepBlue,
                                                                bgcolor: "#EFF6FF",
                                                                borderRadius: "8px",
                                                                "&:hover": { bgcolor: "#DBEAFE" },
                                                            }}
                                                        >
                                                            <Edit sx={{ fontSize: 17 }} />
                                                        </IconButton>
                                                    </Tooltip>

                                                    {userType !== "teacher" && (
                                                        <Tooltip title="Delete grade record">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDelete(item.id)}
                                                                sx={{
                                                                    width: 33,
                                                                    height: 33,
                                                                    color: COLORS.danger,
                                                                    bgcolor: "#FEF2F2",
                                                                    borderRadius: "8px",
                                                                    "&:hover": { bgcolor: "#FEE2E2" },
                                                                }}
                                                            >
                                                                <Delete sx={{ fontSize: 17 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box
                    sx={{
                        borderTop: `1px solid ${COLORS.border}`,
                        bgcolor: "#FAFCFF",
                        px: { xs: 0.5, sm: 1.5 },
                    }}
                >
                    <TablePagination
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        labelRowsPerPage="Rows:"
                        sx={{
                            "& .MuiTablePagination-toolbar": {
                                minHeight: 64,
                                flexWrap: { xs: "wrap", sm: "nowrap" },
                                justifyContent: { xs: "center", sm: "flex-end" },
                                px: { xs: 0.5, sm: 1 },
                                py: 1,
                                gap: 0.5,
                            },
                            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                                fontSize: 11,
                                color: COLORS.muted,
                                fontWeight: 650,
                            },
                            "& .MuiTablePagination-select": {
                                fontSize: 12,
                                fontWeight: 750,
                            },
                            "& .MuiTablePagination-actions button": {
                                color: COLORS.deepBlue,
                                borderRadius: "8px",
                            },
                        }}
                    />
                </Box>
            </Paper>

            {/* NOTIFICATIONS */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                sx={{
                    width: { xs: "calc(100% - 24px)", sm: "auto" },
                    bottom: { xs: "12px !important", sm: "24px !important" },
                    left: { xs: "12px !important", sm: "auto" },
                }}
            >
                <Alert
                    severity={alertSeverity}
                    variant="filled"
                    onClose={() => setSnackbarOpen(false)}
                    sx={{
                        width: "100%",
                        borderRadius: "12px",
                        fontWeight: 650,
                        boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
                    }}
                >
                    {alertMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default GradeDistribution;